import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Card,
  CardContent,
  Button,
  IconButton,
  TextField,
  CircularProgress,
  Alert,
  Chip
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import DeleteIcon from '@mui/icons-material/Delete';
import StarIcon from '@mui/icons-material/Star';
import StarBorderIcon from '@mui/icons-material/StarBorder';
import { 
  getSavedFilters, 
  deleteSavedFilter, 
  toggleFavorite
} from '../utils/savedFiltersUtils';

/**
 * SavedFilters component - displays and manages saved filter configurations
 */
const SavedFilters = () => {
  const [filters, setFilters] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  
  // Load saved filters on component mount
  useEffect(() => {
    loadSavedFilters();
  }, []);
  
  // Load saved filters from the API
  const loadSavedFilters = async () => {
    try {
      setLoading(true);
      setError('');
      
      // getSavedFilters already returns the parsed data, not a Response object
      const filters = await getSavedFilters();
      console.log('Loaded saved filters:', filters);
      
      // Set filters directly since we already have the parsed data
      setFilters(Array.isArray(filters) ? filters : []);
    } catch (error) {
      console.error('Error loading saved filters:', error);
      setError(error.message || 'An error occurred while loading saved filters');
    } finally {
      setLoading(false);
    }
  };
  
  // Handle filter deletion
  const handleDeleteFilter = async (filterId) => {
    if (!window.confirm('Are you sure you want to delete this filter?')) {
      return;
    }
    
    try {
      setError('');
      
      // deleteSavedFilter already returns the parsed result
      await deleteSavedFilter(filterId);
      console.log(`Filter ${filterId} deleted successfully`);
      
      // Remove deleted filter from state
      setFilters(filters.filter(filter => filter.id !== filterId));
    } catch (error) {
      console.error('Error deleting filter:', error);
      setError(error.message || 'An error occurred while deleting the filter');
    }
  };
  
  // Handle toggling favorite status
  const handleToggleFavorite = async (filterId) => {
    try {
      setError('');
      
      // Call the toggleFavorite function with the filter ID
      // This already returns the parsed result
      const result = await toggleFavorite(filterId);
      console.log('Toggle favorite result:', result);
      
      // Check if the result is valid
      if (!result || !result.filter) {
        throw new Error('Failed to update favorite status: Invalid response');
      }
      
      // Update filter in state
      setFilters(filters.map(filter => 
        filter.id === filterId 
          ? { ...filter, is_favorite: result.filter.is_favorite } 
          : filter
      ));
    } catch (error) {
      console.error('Error toggling favorite:', error);
      setError(error.message || 'An error occurred while updating favorite status');
    }
  };
  
  // No apply filter functionality here - all filtering is done from the upload page
  
  // Filter the list based on search term
  const filteredFilters = filters.filter(filter => 
    filter.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (filter.description && filter.description.toLowerCase().includes(searchTerm.toLowerCase()))
  );
  
  // Sort filters: favorites first, then by created date (newest first)
  const sortedFilters = [...filteredFilters].sort((a, b) => {
    if (a.is_favorite && !b.is_favorite) return -1;
    if (!a.is_favorite && b.is_favorite) return 1;
    
    return new Date(b.created_at) - new Date(a.created_at);
  });
  
  return (
    <Box sx={{ width: '100%', p: 2 }}>
      
      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}
      
      <Box sx={{ mb: 3, display: 'flex', alignItems: 'center' }}>
        <TextField
          variant="outlined"
          size="small"
          placeholder="Search filters..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          sx={{ width: 300 }}
          InputProps={{
            startAdornment: <SearchIcon color="action" sx={{ mr: 1 }} />
          }}
        />
      </Box>
      
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 5 }}>
          <CircularProgress />
        </Box>
      ) : sortedFilters.length > 0 ? (
        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr', gap: 2 }}>
          {sortedFilters.map(filter => (
            <Card 
              key={filter.id} 
              variant="outlined" 
              sx={{ 
                overflow: 'visible',
                borderRadius: '8px',
                bgcolor: '#161c3e', // User-specified darker blue background
                borderColor: 'rgba(255,255,255,0.1)',
                '&:hover': {
                  borderColor: 'rgba(255,255,255,0.2)',
                  boxShadow: '0 0 8px rgba(0,127,255,0.2)'
                },
                transition: 'all 0.2s ease-in-out'
              }}
            >
              <CardContent sx={{ py: 2, px: 2.5, '&:last-child': { pb: 2 } }}>
                <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
                  {/* Left side: Filter name and details */}
                  <Box sx={{ display: 'flex', flexDirection: 'column', width: 'calc(100% - 40px)' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                      <IconButton 
                        size="small" 
                        onClick={() => handleToggleFavorite(filter.id)}
                        sx={{ 
                          mr: 0.5, 
                          p: 0.5, 
                          color: filter.is_favorite ? '#F7BF50' : 'rgba(255,255,255,0.5)' 
                        }}
                      >
                        {filter.is_favorite ? <StarIcon fontSize="small" /> : <StarBorderIcon fontSize="small" />}
                      </IconButton>
                      <Typography variant="subtitle1" component="div" sx={{ fontWeight: 600, color: 'primary.light' }}>
                        {filter.name}
                      </Typography>
                    </Box>
                    
                    {filter.description && (
                      <Typography variant="body2" color="text.secondary" sx={{ ml: 4, mb: 0.5 }}>
                        {filter.description}
                      </Typography>
                    )}
                    
                    {/* Meta info row */}
                    <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1, ml: 0.5, mt: 0.5, mb: 1 }}>
                      <Chip 
                        size="small" 
                        label={`${filter.filter_config.selectedColumns?.length || 0} columns`} 
                        sx={{ 
                          height: 20, 
                          fontSize: '0.7rem',
                          background: 'rgba(0,127,255,0.15)',
                          color: 'primary.light',
                          borderRadius: '4px'
                        }}
                      />
                      <Chip 
                        size="small" 
                        label={filter.filter_config.model || 'default model'} 
                        sx={{ 
                          height: 20, 
                          fontSize: '0.7rem',
                          background: 'rgba(232,62,140,0.15)',
                          color: 'secondary.light',
                          borderRadius: '4px'
                        }}
                      />
                      {filter.overrides && Object.keys(filter.overrides).length > 0 && (
                        <Chip 
                          size="small" 
                          label={`${Object.values(filter.overrides).reduce((count, obj) => count + Object.keys(obj).length, 0)} overrides`} 
                          sx={{ 
                            height: 20, 
                            fontSize: '0.7rem',
                            background: 'rgba(171,71,188,0.15)',
                            color: 'wyspur.purple',
                            borderRadius: '4px'
                          }}
                        />
                      )}
                      <Typography variant="caption" color="text.secondary" sx={{ ml: 0.5 }}>
                        {new Date(filter.created_at).toLocaleDateString()}
                      </Typography>
                    </Box>
                  </Box>
                  
                  {/* Right side: Action button */}
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <IconButton 
                      aria-label="Delete filter" 
                      size="small" 
                      onClick={() => handleDeleteFilter(filter.id)}
                      sx={{ 
                        color: 'rgba(255,255,255,0.5)',
                        '&:hover': {
                          color: 'error.light'
                        }
                      }}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          ))}
        </Box>
      ) : (
        <Paper 
          variant="outlined" 
          sx={{ p: 4, textAlign: 'center', borderStyle: 'dashed' }}
        >
          <Typography variant="h6" gutterBottom>
            No saved filters found
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {searchTerm ? 'No filters match your search criteria.' : 'Create and save filter configurations to reuse them later.'}
          </Typography>
          {searchTerm && (
            <Button 
              variant="text" 
              onClick={() => setSearchTerm('')} 
              sx={{ mt: 2 }}
            >
              Clear Search
            </Button>
          )}
        </Paper>
      )}
    </Box>
  );
};

export default SavedFilters;
